import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { login as apiLogin, getCurrentUser, getUserPermissions } from './services/api';
import { isAdmin as checkIsAdmin } from './utils/roleUtils';

interface ViewAsMode {
  isActive: boolean;
  originalUser: any;
  viewAsPermissions: string[];
  viewAsRoles: string[];
  viewAsPrimaryRole: string;
  viewAsClub: string;
}

interface AuthContextType {
  user: any;
  token: string | null;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  loading: boolean;
  refreshUser: () => Promise<void>;
  // View-as mode functionality
  viewAsMode: ViewAsMode;
  enterViewAsMode: (permissions: string[], roles: string[], primaryRole: string, club: string) => void;
  exitViewAsMode: () => void;
  isAdmin: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<any>(null);
  const [token, setToken] = useState<string | null>(() => {
    const storedToken = localStorage.getItem('token');
    return storedToken;
  });
  const [loading, setLoading] = useState(true);
  
  // View-as mode state
  const [viewAsMode, setViewAsMode] = useState<ViewAsMode>({
    isActive: false,
    originalUser: null,
    viewAsPermissions: [],
    viewAsRoles: [],
    viewAsPrimaryRole: '',
    viewAsClub: ''
  });
  
  // Helper to check if current user is admin (uses centralized role checking)
  // Note: This checks the ORIGINAL user's role, not the view-as role
  const isAdmin = checkIsAdmin(user);

  useEffect(() => {
    const fetchUser = async () => {
      if (token) {
        try {
          const res = await getCurrentUser(token);
          const userData = res.data.user;

          // Fetch user's permissions and roles
          try {
            const permRes = await getUserPermissions(userData.member_id);
            setUser({
              ...userData,
              roles: permRes.data.roles,
              permissions: permRes.data.permissions,
              primary_role: permRes.data.primary_role
            });
          } catch (permError) {
            console.error('AuthContext: Failed to fetch permissions:', permError);
            // Still set user even if permissions fail (backward compatibility)
            setUser(userData);
          }
        } catch (error) {
          console.error('AuthContext: Token validation failed:', error);
          setUser(null);
          setToken(null);
          localStorage.removeItem('token');
        }
      } else {
        setUser(null);
      }

      setLoading(false);
    };

    fetchUser();
  }, [token]);

  const login = async (email: string, password: string) => {
    setLoading(true);
    try {
      const res = await apiLogin(email, password);
      setToken(res.data.token);
      localStorage.setItem('token', res.data.token);

      const userData = res.data.user;

      // Fetch user's permissions and roles
      try {
        const permRes = await getUserPermissions(userData.member_id);
        setUser({
          ...userData,
          roles: permRes.data.roles,
          permissions: permRes.data.permissions,
          primary_role: permRes.data.primary_role
        });
      } catch (permError) {
        console.error('AuthContext: Failed to fetch permissions:', permError);
        // Still set user even if permissions fail (backward compatibility)
        setUser(userData);
      }
    } catch (error) {
      console.error('AuthContext: Login failed:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    setUser(null);
    setToken(null);
    localStorage.removeItem('token');
  };

  const refreshUser = async () => {
    if (token) {
      try {
        const res = await getCurrentUser(token);
        const userData = res.data.user;

        // Fetch user's permissions and roles
        try {
          const permRes = await getUserPermissions(userData.member_id);
          setUser({
            ...userData,
            roles: permRes.data.roles,
            permissions: permRes.data.permissions,
            primary_role: permRes.data.primary_role
          });
        } catch (permError) {
          console.error('AuthContext: Failed to fetch permissions:', permError);
          setUser(userData);
        }
      } catch (error) {
        console.error('AuthContext: Error refreshing user data:', error);
        // Don't logout on refresh failure, just log the error
      }
    }
  };

  // View-as mode functions
  const enterViewAsMode = (permissions: string[], roles: string[], primaryRole: string, club: string) => {
    if (!isAdmin) {
      console.error('Only admins can enter view-as mode');
      return;
    }

    const originalUser = user;
    const viewAsUser = {
      ...originalUser,
      permissions: permissions,
      roles: roles,
      primary_role: primaryRole,
      club: club
    };

    setViewAsMode({
      isActive: true,
      originalUser: originalUser,
      viewAsPermissions: permissions,
      viewAsRoles: roles,
      viewAsPrimaryRole: primaryRole,
      viewAsClub: club
    });

    setUser(viewAsUser);
  };

  const exitViewAsMode = () => {
    if (viewAsMode.isActive && viewAsMode.originalUser) {
      setUser(viewAsMode.originalUser);
      setViewAsMode({
        isActive: false,
        originalUser: null,
        viewAsPermissions: [],
        viewAsRoles: [],
        viewAsPrimaryRole: '',
        viewAsClub: ''
      });
    }
  };

  return (
    <AuthContext.Provider value={{ 
      user, 
      token, 
      login, 
      logout, 
      loading, 
      refreshUser,
      viewAsMode,
      enterViewAsMode,
      exitViewAsMode,
      isAdmin
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}; 